import get from 'lodash/get';
import { type ReactNode, useEffect, useState } from 'react';

import { useTranslation } from 'common/hooks/useTranslation';
import { NotificationType, useNotifications } from 'modules/app/notifications';
import { type CurrenciesKey } from 'src/core/config/money';
import type { FormValues } from 'src/core/modules/invoices/components/Invoices/_shared/SupplierEditor/SupplierForm/SupplierForm';
import type { VerificationOfPayeeResult } from 'src/core/modules/verification-of-payee/apis';

import {
  SupplierFormContainer,
  type SupplierFormSectionId,
  type RenderEditorFooterParams,
} from './SupplierForm';
import { SupplierView } from './SupplierView';
import { reshapeSupplier, toApiSupplierDetails } from './reshaper';
import {
  type ApiSupplier,
  type Supplier,
  type UpdatableSupplierDetails,
} from './supplier';

export type SupplierEditorMode = 'creation' | 'edition';

type Props = {
  company: {
    id: string;
    currency: CurrenciesKey;
    country: string;
  };
  supplier?: ApiSupplier;
  warnForUnsavedChanges?: boolean;
  isBankProofFileDisplayed: boolean;
  supportedMode?: SupplierEditorMode[];
  onSave: (
    supplierId: string | undefined,
    payload: Partial<ApiSupplier>,
  ) => Promise<
    | { outcome: 'success' }
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    | { outcome: 'failure'; error: any }
    | { outcome: 'no-change' }
  >;
  onShowBankProofFile: (
    bankProofFile?: Supplier['details']['bankProofFile'],
  ) => void;
  onHideBankProofFile: () => void;
  onTrackSupplierSave?: (suggestionContext: {
    isIbanDisplayed: boolean;
    suggestedIban: string | null;
    isBicDisplayed: boolean;
    suggestedBic: string | null;
  }) => void;
  showBankProofFileInline?: boolean;
  sections?: SupplierFormSectionId[];
  renderEditorFooter?: (params: RenderEditorFooterParams) => ReactNode;
  currentVerificationOfPayee?: VerificationOfPayeeResult;
  onIgnoreVerificationOfPayee?: () => void;
  onFormFieldUpdate?: (field: keyof FormValues, formFields: FormValues) => void;
  isCheckingVerificationOfPayee?: boolean;
};

export const SupplierEditor = ({
  company,
  supplier,
  warnForUnsavedChanges = false,
  isBankProofFileDisplayed = false,
  showBankProofFileInline = false,
  supportedMode = ['creation', 'edition'],
  onSave,
  onShowBankProofFile,
  onHideBankProofFile,
  onTrackSupplierSave,
  sections,
  renderEditorFooter,
  currentVerificationOfPayee,
  onIgnoreVerificationOfPayee,
  onFormFieldUpdate,
  isCheckingVerificationOfPayee,
}: Props) => {
  const { t } = useTranslation('global');
  const { pushNotif } = useNotifications();

  // FIXME: move this reshaping logic into redux actions
  const supplierReshaped = supplier ? reshapeSupplier(supplier) : undefined;
  const supplierId = supplierReshaped ? supplierReshaped.id : undefined;
  const detectedMode: SupplierEditorMode =
    supplierReshaped && supplierReshaped.validatedAt ? 'edition' : 'creation';

  const mode: SupplierEditorMode = supportedMode.includes(detectedMode)
    ? detectedMode
    : supportedMode[0];

  const canToggleBetweenFormAndView =
    supportedMode.includes('edition') && supportedMode.includes('creation');

  const isCreatingNewSupplier = mode === 'creation';
  const shouldShowFormByDefault =
    isCreatingNewSupplier || !canToggleBetweenFormAndView;

  const [isFormDisplayed, setIsFormDisplayed] = useState(
    shouldShowFormByDefault,
  );

  const showSupplierForm = () => setIsFormDisplayed(true);
  const showSupplierView = () => setIsFormDisplayed(false);

  // It's really important to only depend on the supplier ID here to avoid
  // unexpected behavior due to unexpected re-rendering caused by the parent
  // props changes
  useEffect(() => {
    if (shouldShowFormByDefault) {
      showSupplierForm();
    } else {
      showSupplierView();
    }
    return () => onHideBankProofFile();
  }, [supplierId, shouldShowFormByDefault]);

  const handleToggleBankProofFileDisplay = (
    bankProofFile?: Supplier['details']['bankProofFile'],
  ) => {
    if (isBankProofFileDisplayed) {
      return onHideBankProofFile();
    }

    onShowBankProofFile(bankProofFile);
  };

  const handleSaveSupplier = async (
    supplierAccountCode: string | null,
    supplierDetails: UpdatableSupplierDetails,
    bankProofFile?:
      | {
          action: 'upload';
          base64: string;
        }
      | { action: 'delete' },
  ): Promise<{
    outcome: 'success' | 'ibanError' | 'bankProofError' | 'unknownError';
  }> => {
    const onSaveResult = await onSave(supplierId, {
      name: supplier?.name,
      supplierAccountCode: supplierAccountCode?.trim() ?? undefined,
      supplier_details: toApiSupplierDetails(supplierDetails, bankProofFile),
    });

    if (onSaveResult.outcome === 'failure') {
      // Try to check if the failure is due to incorrect IBAN info
      const isIbanError = get(onSaveResult, 'invalidFields.iban');

      if (isIbanError) {
        pushNotif({
          type: NotificationType.Danger,
          message: t('submitMyInvoice.suppliers.invalidBankInfo'),
        });
        return { outcome: 'ibanError' };
      }

      const isBankProofMimeTypeError =
        onSaveResult.error === 'mimeTypeNotSupported';
      if (isBankProofMimeTypeError) {
        pushNotif({
          type: NotificationType.Danger,
          message: t('submitMyInvoice.suppliers.invalidMimeTypeForBankProof', {
            uploadedMimeType: get(
              onSaveResult,
              'errorDetails.uploadedMimeType',
            ),
            allowedMimeTypes: get(
              onSaveResult,
              'errorDetails.allowedMimeTypes',
              [],
            ).join(','),
          }),
        });
        return { outcome: 'bankProofError' };
      }

      pushNotif({
        type: NotificationType.Danger,
        message: t('submitMyInvoice.suppliers.saveFailure'),
      });
      return { outcome: 'unknownError' };
    }

    showSupplierView();

    if (onSaveResult.outcome === 'no-change') {
      return { outcome: 'success' };
    }

    if (mode === 'creation') {
      pushNotif({ message: t('submitMyInvoice.suppliers.createSuccess') });
    } else {
      pushNotif({ message: t('submitMyInvoice.suppliers.saveSuccess') });
    }

    return { outcome: 'success' };
  };

  const shouldShowSupplierView =
    supplierReshaped && !isFormDisplayed && canToggleBetweenFormAndView;

  return shouldShowSupplierView ? (
    <SupplierView
      supplier={supplierReshaped}
      onEditSupplierClicked={showSupplierForm}
    />
  ) : (
    <SupplierFormContainer
      mode={mode}
      company={company}
      supplier={supplierReshaped}
      isBankProofFileDisplayed={isBankProofFileDisplayed}
      warnForUnsavedChanges={warnForUnsavedChanges}
      onShowBankProofFile={handleToggleBankProofFileDisplay}
      onSaveSupplier={handleSaveSupplier}
      onTrackSupplierSave={onTrackSupplierSave}
      showBankProofFileInline={showBankProofFileInline}
      sections={sections}
      renderSupplierFormFooter={renderEditorFooter}
      verificationOfPayeeResult={currentVerificationOfPayee}
      onIgnoreVoP={onIgnoreVerificationOfPayee}
      onFormFieldUpdate={onFormFieldUpdate}
      isCheckingVerificationOfPayee={isCheckingVerificationOfPayee}
    />
  );
};
